<template>
  <div>
    <!-- Template para cuando la orden existe y está rechazada -->
    <div v-if="order && order.status !== 'finished'">
      <layout-default :hide-footer-mobile="true">
        <div class="purchase-confirmation">
          <div class="container">
            
            <!-- Error Banner -->
            <div class="error-banner">
              <img src="/images/featured/close-circle.svg" alt="error" />
              <div class="banner-content">
                <h6>Tu compra fue rechazada</h6>
                <p>No se pudo procesar tu pago. Por favor, verifica tu información de pago e intenta nuevamente.</p>
              </div>
            </div>

            <!-- Main Content - Two Columns -->
            <div class="main-content">
              
              <!-- Left Column - Payment Information -->
              <div class="column payment-info">
                <h2>Información de pago</h2>
                <div class="info-card">
                  <div class="info-item">
                    <span>Nº Orden de pedido</span>
                    <p>{{ order.buyOrder }}</p>
                  </div>
                  <div class="info-item">
                    <span>Nombre del comercio</span>
                    <p>Preuniversitario Pedro de Valdivia SA</p>
                  </div>
                  <div class="info-item">
                    <span>Monto y moneda</span>
                    <p>${{ formatCurrency(getActualPaidAmount()) }}</p>
                  </div>
                  <div class="info-item">
                    <span>Código de cupón</span>
                    <p>{{ getCouponCode() }}</p>
                  </div>
                  <div class="info-item">
                    <span>Descuento aplicado</span>
                    <p>{{ getCouponDiscountPercentage() }}%</p>
                  </div>
                  <div class="info-item">
                    <span>Fecha de transacción</span>
                    <p>{{ formatDate(order.createdAt) }}</p>
                  </div>
                  <div class="info-item">
                    <span>Descripción de los bienes y/o servicios</span>
                    <p>Programas PAES</p>
                  </div>
                </div>
              </div>

              <!-- Right Column - Purchase Information -->
              <div class="column purchase-info">
                <h2>Información de tu compra</h2>
                <div class="info-card">
                  <div class="info-item">
                    <span>Programa</span>
                    <p>{{ getProgramName() }}</p>
                  </div>
                  <div class="info-item">
                    <span>Nombre del alumno</span>
                    <p>{{ getStudentName() }}</p>
                  </div>
                  <div class="info-item" v-if="getStudentLocation()">
                    <span>Sede</span>
                    <p>{{ getStudentLocation() }}</p>
                  </div>
                  
                  <!-- Courses Section -->
                  <div class="courses-section">
                    <span class="section-title">Cursos</span>
                    <div 
                      v-for="(course, index) in getStudentCourses()" 
                      :key="index"
                      class="course-item"
                    >
                      <div class="course-header">
                        <div class="course-icon" :class="getCourseColor(index)"></div>
                        <span>{{ course.name }}</span>
                      </div>
                      <p class="course-schedule">{{ course.schedule }}</p>
                    </div>
                  </div>

                  <!-- Cost Summary -->
                  <div class="cost-summary">
                                    <div class="cost-item">
                    <span>Total cursos</span>
                    <span>${{ formatCurrency(getTotalCourses()) }}</span>
                  </div>
                  <div class="cost-item discount" v-if="getDiscountAmount() > 0">
                    <span>Desc. Inscripción online ({{ getDiscountPercentage() }}%)</span>
                    <span>-{{ formatCurrency(getDiscountAmount()) }}</span>
                  </div>
                  <div class="cost-item discount" v-if="getCouponDiscountAmount() > 0">
                    <span>Desc. Cupón {{ getCouponCode() }} ({{ getCouponDiscountPercentage() }}%)</span>
                    <span>-{{ formatCurrency(getCouponDiscountAmount()) }}</span>
                  </div>
                  <div class="cost-item">
                    <span>Matrícula online</span>
                    <span>${{ formatCurrency(getEnrollmentFee()) }}</span>
                  </div>
                  <div class="cost-item total">
                    <span>Monto programa</span>
                    <span>${{ formatCurrency(total) }}</span>
                  </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Top Separator -->
            <div class="top-separator"></div>

            <!-- Total Paid Section -->
            <div class="total-paid">
              <span>Total a pagar</span>
              <span class="amount">${{ formatCurrency(total) }}</span>
            </div>

            <!-- Separator -->
            <div class="separator"></div>

            <!-- Main Action Button -->
            <div class="main-action">
              <button @click="retryPayment" class="btn-primary">
                Reintentar pago
              </button>
              <button @click="goBack" class="btn-secondary">
                Volver
              </button>
            </div>

          </div>
        </div>
      </layout-default>
      
      <!-- Modal de Procesamiento de Pago -->
      <div v-if="processingModal" class="modal-intensive" id="modalIntensive">
        <div class="content realtive">
          <img
            src="/images/featured/imageModalLoading.svg"
            alt="imageModalLoading"
            class="img-modal-loading"
          />
          <div class="text-content">
            <div class="d-flex justify-content-center mb-4">
              <img
                src="/images/featured/IconButtonRelod.svg"
                alt="IconButtonRelod"
              />
            </div>
            <h5>Espera un momento mientras procesamos tu solicitud</h5>
            <p>Esto puede demorar unos segundos.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Template para cuando la orden no existe -->
    <div v-else>
      <layout-default :hide-footer-mobile="true">
        <div class="purchase-confirmation">
          <div class="container">
            <div class="error-banner">
              <img src="/images/featured/close-circle.svg" alt="error" />
              <div class="banner-content">
                <h6>Orden no encontrada</h6>
                <p>La orden solicitada no existe o ha expirado. Por favor, verifica el número de orden e intenta nuevamente.</p>
              </div>
            </div>
            
            <div class="main-action">
              <button @click="goToLanding" class="btn-primary">
                Ir al inicio
              </button>
            </div>
          </div>
        </div>
      </layout-default>
    </div>
  </div>
</template>

<script>
import LayoutDefault from "@/layouts/Default";
import { orderBy } from "@/helpers";
import formValidation from "@/mixins/formValidation";
import { getRandomColor } from '@/constants/colors';
import { getCourseColorClassName } from '@/utils/colorUtils';
import { formatFullCourseName } from '@/utils/subjectNameUtils';
import { getLocationDisplayName } from '@/utils/locationName';

export default {
  components: {
    LayoutDefault
  },
  data() {
    return {
      total: null,
      order: null,
      resendLoading: false,
      discount: null,
      serverPath: process.env.VUE_APP_API_URL,
      processingModal: false
    };
  },

  computed: {
    formFinancingParty() {
      return {
        ...this.$store.state.financingParty
      };
    },
    financingPartyName() {
      return `${this.$store.state.financingParty.name} ${this.$store.state.financingParty.lastName}`;
    },
    financingPartyRut() {
      return this.$store.state.financingParty.rut;
    },
    financingPartyEmail() {
      return this.$store.state.financingParty.email;
    },
    financingPartyRegion() {
      if (!this.$store.state.financingParty.Region) return undefined;
      return this.$store.state.financingParty.Commune.Region.name;
    },
    financingPartyCity() {
      if (!this.$store.state.financingParty.City) return undefined;
      return this.$store.state.financingParty.City.name;
    },
    financingPartyCommune() {
      if (!this.$store.state.financingParty.Commune) return undefined;
      return this.$store.state.financingParty.Commune.name;
    },
    students() {
      return this.$store.state.students;
    },
    financingParty() {
      return this.$store.state.financingParty;
    },
    selectedPack(){
      return JSON.parse(localStorage.getItem('pack'));
    }
  },

  mixins: [formValidation],

  methods: {
    goToNextStep() {
      this.$router.push({ name: "SuccessInscription" });
    },
    goToLanding() {
      this.$router.push({ name: "Landing" });
    },
    async retryPayment() {
      try {
        // Mostrar modal de procesamiento
        this.processingModal = true;
        
        // Verificar que los datos requeridos estén disponibles
        if (!this.order || !this.students || this.students.length === 0) {
          throw new Error('Datos de la orden no están disponibles. Por favor, completa el proceso nuevamente.');
        }
        
        // Reconstruir los datos de la orden para el reintento
        const orderData = await this.buildOrderDataForRetry();
        
        // Actualizar la orden existente
        const orderResponse = await this.Axios.put(`/orders`, orderData);
        
        // Redirigir a Transbank con la nueva información
        this.redirectToTransbank(orderResponse.data);
        
      } catch (error) {
        console.error('Error al reintentar el pago:', error);
        this.processingModal = false;
        
        this.$swal.fire({
          position: "center",
          icon: "error",
          title: "Error al reintentar",
          text: error.message || "Hubo un problema al procesar tu solicitud. Por favor, intenta nuevamente.",
          showConfirmButton: true,
          confirmButtonText: "Aceptar"
        });
      }
    },
    contactSupport() {
      // Abrir chat de soporte o redirigir a página de contacto
      window.open('mailto:soporte@pdv.cl', '_blank');
    },
    goBack() {
      // Volver a la página principal
      this.$router.push({ name: "Landing" });
    },
    
    // Construir datos de la orden para reintento reutilizando información existente
    async buildOrderDataForRetry() {
      // Reutilizar la información existente del orden
      const orderData = this.order;
      
      // Extraer los courseIds únicos de OrderDetails
      const uniqueCourseIds = [...new Set(
        orderData.OrderDetails?.map(detail => detail.courseId) || []
      )];
      
      // Validar que tengamos al menos un curso válido
      if (uniqueCourseIds.length === 0) {
        throw new Error('No se encontraron cursos válidos para procesar el pago.');
      }
      
      // Agrupar todos los cursos en una sola entrada si pertenecen al mismo programa, ubicación y estudiante
      const firstDetail = orderData.OrderDetails?.[0];
      if (!firstDetail) {
        throw new Error('No se encontraron detalles de la orden.');
      }
      
      return {
        sessionState: JSON.stringify(orderData.sessionState),
        orderDetails: [{
          locationId: firstDetail.locationId,
          programId: firstDetail.programId,
          coursesIds: uniqueCourseIds.map(id => String(id)), // Convertir a string y agrupar todos los cursos
          studentId: firstDetail.studentId
        }],
        coupon: orderData.coupon,
        rut: orderData.FinancingParty?.rut,
        financingParty: orderData.FinancingParty
      };
    },
    
    // Redirigir a Transbank (mismo método que en Step5)
    redirectToTransbank(orderResponse) {
      this.processingModal = false;
      
      if (orderResponse.url && orderResponse.token) {
        // Crear formulario para enviar a Transbank
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = orderResponse.url;
        form.style.display = 'none';
        
        const tokenInput = document.createElement('input');
        tokenInput.type = 'hidden';
        tokenInput.name = 'token_ws';
        tokenInput.value = orderResponse.token;
        form.appendChild(tokenInput);
        
        document.body.appendChild(form);
        form.submit();
      } else {
        // Si no hay URL de Transbank, mostrar error
        this.$swal.fire({
          position: "center",
          icon: "error",
          title: "Error en el proceso",
          text: "No se pudo generar la URL de pago. Por favor, contacta soporte.",
          showConfirmButton: true,
          confirmButtonText: "Aceptar"
        });
      }
    },
    getProgramName() {
      if(this.students.length > 0){
        return this.students[0].program.name;
      }
      return '';
    },
    getStudentName() {
      if(this.students.length > 0){
        const student = this.students[0];
        const name = student.name || '';
        const fatherLastName = student.fatherLastName || '';
        const motherLastName = student.motherLastName || '';
        
        // Construir nombre completo concatenando nombre y apellidos
        let fullName = name.trim();
        if (fatherLastName) {
          fullName += ' ' + fatherLastName.trim();
        }
        if (motherLastName) {
          fullName += ' ' + motherLastName.trim();
        }
        
        return fullName.trim();
      }
      return '';
    },
    getStudentLocation() {
      // Obtener sede directamente de los datos de la API (order.sessionState.students)
      if(this.order && this.order.sessionState && this.order.sessionState.students && this.order.sessionState.students.length > 0) {
        const student = this.order.sessionState.students[0];
        if(student.selectedLocation) {
          return getLocationDisplayName(student.selectedLocation);
        } else if(student.preferredVenue) {
          return getLocationDisplayName(student.preferredVenue);
        }
      }
      return 'Sede no especificada';
    },
    getStudentCourses() {
      // MISMA LÓGICA QUE PurchaseConfirmation.vue
      if(this.order?.OrderDetails && this.order.OrderDetails.length > 0){
        return this.order.OrderDetails.map(orderDetail => {
          const course = orderDetail.Course;
          const originalSubjectName = course?.name || '';
          
          // BUSCAR HORARIO EN sessionState.students
          let schedule = 'Horario no disponible';
          if(this.students && this.students.length > 0) {
            const student = this.students[0];
            if(student.selectedCourses) {
              const studentCourse = student.selectedCourses.find(sc => 
                String(sc.courseId) === String(course?.id) || String(sc.id) === String(course?.id)
              );
              
              if(studentCourse) {
                // Obtener horario del curso del estudiante
                if(studentCourse.hour) {
                  schedule = this.getSchedule(studentCourse.hoursPerDay);
                } else if(studentCourse.hoursPerDay) {
                  schedule = this.getSchedule(studentCourse.hoursPerDay);
                } else if(studentCourse.schedule) {
                  schedule = studentCourse.schedule;
                }
              }
            }
          }
          
          // VALIDAR SI ES ELECTIVO SOLO POR EL NOMBRE QUE CONTENGA "ELECTIVO"
          const isElectiveByName = originalSubjectName.toLowerCase().includes('electivo');
          
          const courseWithLabels = {
            ...course,
            hasMention: course?.hasMention || false,
            isElective: isElectiveByName,
            isCommon: course?.isCommon || false
          };
          
          // Usar utilidad compartida para formatear nombre completo
          const formattedName = this.formatFullCourseName(originalSubjectName, courseWithLabels);
          
          return {
            name: formattedName,
            schedule: schedule,
            courseId: course?.id || orderDetail.courseId,
            originalName: originalSubjectName
          };
        });
      }
      return [];
    },

    formatFullCourseName(originalSubjectName, course) {
      // Usar utilidad centralizada para evitar duplicación de código
      return formatFullCourseName(originalSubjectName, course);
    },
    getCourseColor(index) {
      // Usar la utilidad reutilizable para obtener el nombre de clase CSS
      return getCourseColorClassName(index, null, '', getRandomColor);
    },
    getTotalCourses() {
      if(this.students.length > 0 && this.students[0].prices){
        return this.students[0].prices.precioSubTotal || 0;
      }
      return 0;
    },
    getDiscountPercentage() {
      // Calcular el porcentaje de descuento general (sin cupón)
      if(this.students.length > 0 && this.students[0].prices){
        const prices = this.students[0].prices;
        const precioSubTotal = prices.precioSubTotal || 0;
        const valorDescuento = prices.valorDescuento || 0;
        
        if (precioSubTotal > 0) {
          return Math.round((valorDescuento / precioSubTotal) * 100);
        }
      }
      return 0;
    },
    getDiscountAmount() {
      if(this.students.length > 0 && this.students[0].prices){
        const prices = this.students[0].prices;
        // Solo el descuento general (sin cupón)
        return prices.valorDescuento || 0;
      }
      return 0;
    },
    getCouponDiscountAmount() {
      if(this.students.length > 0 && this.students[0].prices){
        const prices = this.students[0].prices;
        // Solo el descuento del cupón
        return prices.valorDescuentoCupon || 0;
      }
      return 0;
    },
    getCouponDiscountPercentage() {
      // Detectar específicamente cupones del 100% - PRIORIDAD MÁXIMA
      const cupon = this.getCouponCode();
      if (cupon && cupon.toUpperCase().includes('100')) {
        return 100;
      }
      
      if(this.students.length > 0 && this.students[0].prices){
        const prices = this.students[0].prices;
        const precioSubTotal = prices.precioSubTotal || 0;
        const valorDescuento = prices.valorDescuento || 0;
        const valorDescuentoCupon = prices.valorDescuentoCupon || 0;
        const precioTotal = prices.precioTotal || 0;
        
        // Si el precio total es 0, es un cupón del 100%
        if (precioTotal === 0) {
          return 100;
        }
        
        // Calcular el subtotal después del primer descuento (matrícula online)
        const subtotalDespuesPrimerDescuento = precioSubTotal - valorDescuento;
        
        // Si el descuento del cupón es igual al subtotal restante, es 100%
        if (subtotalDespuesPrimerDescuento > 0 && valorDescuentoCupon >= subtotalDespuesPrimerDescuento) {
          return 100;
        }
        
        // El porcentaje del cupón debe calcularse sobre el subtotal restante
        if (subtotalDespuesPrimerDescuento > 0 && valorDescuentoCupon > 0) {
          return Math.round((valorDescuentoCupon / subtotalDespuesPrimerDescuento) * 100);
        }
      }
      return 0;
    },
    getTotalDiscountPercentage() {
      if(this.students.length > 0 && this.students[0].prices){
        // Sumar los porcentajes individuales de descuento
        const descuentoOnline = this.getDiscountPercentage();
        const descuentoCupon = this.getCouponDiscountPercentage();
        
        return descuentoOnline + descuentoCupon;
      }
      return 0;
    },
    getActualPaidAmount() {
      // Obtener el monto real pagado desde los datos del estudiante
      if(this.students.length > 0 && this.students[0].prices){
        return this.students[0].prices.precioTotal || 0;
      }
      // Fallback al total del order si no hay datos del estudiante
      return this.order?.total || 0;
    },
    getCouponCode() {
      // Obtener el código del cupón desde los datos del estudiante
      if(this.students.length > 0 && this.students[0].prices){
        const cupon = this.students[0].prices.cupon;
        const validCupon = this.students[0].prices.validCupon;
        
        // Si hay cupón pero no es válido, mostrar "Sin cupón"
        if (cupon && !validCupon) {
          return 'Sin cupón';
        }
        
        // Si hay cupón y es válido, mostrarlo
        if (cupon && validCupon) {
          return cupon;
        }
        
        return 'Sin cupón';
      }
      // Fallback al cupón del order si no hay datos del estudiante
      return this.order?.coupon || 'Sin cupón';
    },
    getEnrollmentFee() {
      if(this.students.length > 0 && this.students[0].prices){
        return this.students[0].prices.valorMatricula || 0;
      }
      return 0;
    },
    formatDate(dateString) {
      if(!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        console.error('Error formateando fecha:', error);
        return '';
      }
    },
    formatCurrency(amount) {
      if(!amount) return '0';
      return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    setTotal() {
      if(this.selectedPack){
        this.total = this.selectedPack.priceOfert;
      } else {
        // Usar el monto real pagado (con descuentos aplicados)
        this.total = this.$store.state.students
        .map(student => student.prices.precioTotal)
        .reduce((previousValue, currentValue) => previousValue + currentValue, 0);
      }
    },
    getSchedule(hoursPerDay){
      if(!hoursPerDay || hoursPerDay.length === 0) return '';
      
      let days = "";
      if (hoursPerDay.length == 1) {
        days = hoursPerDay[0].day + " " + hoursPerDay[0].startTime.slice(0, -3) + " a " + hoursPerDay[0].endTime.slice(0, -3);
      } else if (hoursPerDay.length == 2) {
        if (hoursPerDay[0].startTime.slice(0, -3) == hoursPerDay[1].startTime.slice(0, -3) && hoursPerDay[0].endTime.slice(0, -3) == hoursPerDay[1].endTime.slice(0, -3)) {
          days = hoursPerDay[0].day + " y " + hoursPerDay[1].day + " " + hoursPerDay[0].startTime.slice(0, -3) + " a " + hoursPerDay[0].endTime.slice(0, -3);
        } else {
          days = hoursPerDay[0].day + " " + hoursPerDay[0].startTime.slice(0, -3) + " a " + hoursPerDay[0].endTime.slice(0, -3) + " y " + hoursPerDay[1].day + " " + hoursPerDay[1].startTime.slice(0, -3) + " a " + hoursPerDay[1].endTime.slice(0, -3);
        }
      }
      return days;
    },
    downloadPdf() {
      window.open(
        `${this.serverPath}/tools/comprobante-compra?buyorder=${this.order.buyOrder}&isDownload=1`,
        "_self"
      );
    },
    downloadRegulations() {
      if(process.env.VUE_APP_PDV_RULEBOOK) {
        window.open(process.env.VUE_APP_PDV_RULEBOOK, "_blank");
      }
    },
    async resendEmail() {
      this.resendLoading = true;
      try {
        const { data } = await this.Axios.post("/resend", {
          order: this.order.buyOrder
        });
        if (data.success) {
          alert(`Se ha reenviado el email a ${this.financingPartyEmail}`);
        } else {
          alert(`Ha ocurrido un problema al intentar reenviar el email`);
        }
      } catch (error) {
        alert(`Ha ocurrido un problema al intentar reenviar el email`);
      }
      setTimeout(() => {
        this.resendLoading = false;
      }, 1000);
    }
  },

  async created() {
    this.Axios.get("/dashadmin/infogeneral").then(response => {
      this.discount = response.data[0].discount;
    });
    
    const { data: regions } = await this.Axios.get("/regions");
    this.availableRegions = orderBy([...regions], "name");
    
    this.Axios.get("/orders" + window.location.search).then(async response => {
      // Verificar si la respuesta es un array vacío o no tiene datos
      if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
        this.order = null;
        // No mostrar modal, dejar que el template maneje la visualización
        return;
      }

      this.order = response.data;
      if (response.data.status === "rejected") {
        // Mostrar vista de rechazo
      }
      await this.$store.dispatch("setOrderInfo", response.data);

      await this.$store.dispatch(
        "addFinancingPartyInfo",
        response.data.FinancingParty
      );

      await this.$store.dispatch(
        "addCaretakerInfo",
        response.data.OrderDetails[0].Student.Caretaker
      );

      this.$store
        .dispatch("setStudents", response.data.sessionState.students)
        .then(() => {
          this.setTotal();
        });
    }).catch(error => {
      console.error('Error al obtener la orden:', error);
      this.order = null;
      // No mostrar modal, dejar que el template maneje la visualización
    });
  },

  destroyed() {
    localStorage.clear();
  },
  beforeDestroy() {
    localStorage.clear();
  },
};
</script>

<style scoped lang="scss">
.purchase-confirmation {
  padding: 40px 0;
  background: #fff;
  min-height: 100vh;

  .container {
    max-width: 936px;
    margin: 0 auto;
    padding: 0 24px;

    @media (min-width: 769px) {
      padding: 0 76px;
    }
  }

  // Error Banner
  .error-banner {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    background: #FFF5F5;
    border: 1px solid #F56565;
    border-radius: 8px;
    margin-bottom: 40px;

    img {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }

    .banner-content {
      h6 {
        font-family: 'Poppins-Medium', sans-serif;
        color: #C53030;
        font-size: 18px;
        font-weight: 500;
        line-height: 26px;
        margin: 0 0 4px 0;
      }

      p {
        color: #C53030;
        font-family: 'Poppins-Regular', sans-serif;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        margin: 0;

        span {
          font-family: 'Poppins-Bold', sans-serif;
          font-weight: 700;
          color: #C53030;
        }
      }
    }
  }

  // Main Content - Two Columns
  .main-content {
    display: flex;
    gap: 40px;
    margin-bottom: 40px;

    @media (max-width: 768px) {
      flex-direction: column;
    }

    .column {
      flex: 1;

      h2 {
        color: #000;
        font-family: 'Poppins-Bold', sans-serif;
        font-size: 36px;
        font-weight: 700;
        line-height: 46px;
        margin: 0 0 24px 0;

        @media (max-width: 768px) {
          font-size: 20px;
          line-height: 28px;
        }
      }

      .info-card {
        background: #fff;
        padding: 16px;

        .info-item {
          display: flex;
          flex-direction: column;
          margin-bottom: 12px;

          &:last-of-type {
            margin-bottom: 0;
          }

          span {
            color: #666;
            font-family: 'Poppins-Regular', sans-serif;
            font-size: 16px;
            font-weight: 400;
            line-height: 24px;
            margin-bottom: 4px;
          }

          p {
            color: #000;
            font-family: 'Poppins-Medium', sans-serif;
            font-size: 18px;
            font-weight: 500;
            line-height: 26px;
            margin: 0;
          }
        }

        // Courses Section
        .courses-section {
          margin: 24px 0;

          .section-title {
            color: #666;
            font-family: 'Poppins-Bold', sans-serif;
            font-size: 16px;
            font-weight: 700;
            line-height: 24px;
            display: block;
            margin-bottom: 12px;
          }

          .course-item {
            margin-bottom: 16px;

            &:last-child {
              margin-bottom: 0;
            }

            .course-header {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 4px;

              .course-icon {
                width: 12px;
                height: 12px;
                border-radius: 50%;

                &.teal {
                  background: #20B2AA;
                }

                &.orange {
                  background: #FFA500;
                }
              }

              span {
                color: #000;
                font-family: 'Poppins-Medium', sans-serif;
                font-size: 18px;
                font-weight: 500;
                line-height: 26px;
              }
            }

            .course-schedule {
              color: #666;
              font-family: 'Poppins-Regular', sans-serif;
              font-size: 16px;
              font-weight: 400;
              line-height: 24px;
              margin: 0;
              padding-left: 20px;
            }
          }
        }

        // Cost Summary
        .cost-summary {
          border-top: 1px dashed #CACBCE;
          padding-top: 24px;

          .cost-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;

            &:last-child {
              margin-bottom: 0;
            }

            span {
              font-family: 'Poppins-Regular', sans-serif;
              font-size: 16px;
              font-weight: 400;
              line-height: 24px;
            }

            &:first-child {
              span {
                color: #000;
              }
            }

            &.discount {
              span {
                color: #0F8047;
                font-size: 14px;
                line-height: 18px;
              }
            }

            &.total {
              span {
                color: #000;
                font-size: 18px;
                line-height: 26px;

                &:last-child {
                  color: #000;
                  font-family: 'Poppins-Bold', sans-serif;
                  font-size: 24px;
                  font-weight: 700;
                  line-height: 32px;
                }
              }
            }
          }
        }
      }
    }
  }

  // Total Paid Section
  .total-paid {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;

    span {
      font-family: 'Poppins', sans-serif;
      font-size: 24px;
      font-weight: 400;
      line-height: 32px;
      color: #666;
      letter-spacing: 0px;

      &.amount {
        font-family: 'Poppins', sans-serif;
        font-size: 24px;
        font-weight: 700;
        line-height: 32px;
        color: #000;
        letter-spacing: 0px;
      }
    }
  }

  // Top Separator
  .top-separator {
    width: 100%;
    height: 1px;
    background: #ccc;
    margin: 40px 0 24px 0;
  }

  // Separator
  .separator {
    width: 100%;
    height: 1px;
    background: repeating-linear-gradient(
      to right,
      #ccc 0,
      #ccc 4px,
      transparent 4px,
      transparent 8px
    );
    margin-bottom: 32px;
  }

  // Action Links
  .action-links {
    display: flex;
    justify-content: center;
    gap: 48px;
    margin-bottom: 32px;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: center;
      gap: 16px;
    }

    .action-link {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #3691D8;
      font-family: 'Poppins-Medium', sans-serif;
      font-size: 16px;
      font-weight: 500;
      line-height: 24px;
      text-decoration: none;
      transition: opacity 0.2s;
      white-space: nowrap;

      &:hover {
        opacity: 0.8;
      }

      img {
        width: 18px;
        height: 18px;
        filter: brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%);
      }
    }
  }

  // Main Action Button
  .main-action {
    display: flex;
    justify-content: center;
    gap: 16px;

    .btn-primary {
      display: flex;
      align-items: center;
      gap: 8px;
      background: #000;
      color: #fff;
      border: none;
      border-radius: 50px;
      padding: 16px 32px;
      font-family: 'Poppins-Medium', sans-serif;
      font-size: 18px;
      font-weight: 500;
      line-height: 26px;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background: #333;
      }

      img {
        width: 18px;
        height: 18px;
      }
    }

    .btn-secondary {
      display: flex;
      align-items: center;
      gap: 8px;
      background: #f5f5f5;
      color: #000;
      border: none;
      border-radius: 50px;
      padding: 16px 32px;
      font-family: 'Poppins-Medium', sans-serif;
      font-size: 18px;
      font-weight: 500;
      line-height: 26px;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background: #e0e0e0;
      }
    }
  }
}

/* Estilos para el modal de procesamiento */
.modal-intensive {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  z-index: 1000;
  overflow: hidden;
  display: flex;
  align-items: center;
  height: 100vh;
  background: rgba(134, 133, 133, 0.1);
}

.modal-intensive .content {
  position: relative;
  width: 40%;
  border-radius: 24px;
  border: 2px solid #000;
  background: #fff;
  padding: 24px;
  box-shadow: 0 0 12px 3px #00000038;
  min-height: 380px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (max-width: 768px) {
  .modal-intensive .content {
    width: 90%;
  }
}

.modal-intensive .text-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding: 0 70px;
}

.modal-intensive .text-content h5 {
  color: #000;
  text-align: center;
  font-family: "Poppins-Bold", sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  margin-bottom: 10px;
}

.modal-intensive .text-content p {
  color: #666;
  text-align: center;
  font-family: "Poppins-Regular", sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  margin-bottom: 0px;
}

.img-modal-loading {
  position: absolute;
  right: 20px;
  bottom: 20px;
  height: 70%;
}

@media (max-width: 768px) {
  .img-modal-loading {
    display: none;
  }
}
</style>
